import { usePassiveApiActivity } from './usePassiveApiActivity';
import { useEffect, useRef } from 'react';

import { apiService } from '../services/api';
import {
  applyVideoDeletionStatus,
  clearVideoDeletionIfMatching,
  PendingVideoDeletionMap,
} from '../utils/videoDeletionState';

type PendingUpdater = (
  updater: (current: PendingVideoDeletionMap) => PendingVideoDeletionMap,
) => void;

const retryDelaySeconds = (pending: PendingVideoDeletionMap): number => (
  Math.min(
    ...Object.values(pending).map((entry) => (
      Math.max(1, Math.min(entry.retry_after_seconds || 5, 30))
    )),
  )
);

export const useVideoDeletionPolling = (
  pending: PendingVideoDeletionMap,
  updatePending: PendingUpdater,
): void => {
  const canObserve = usePassiveApiActivity(Object.keys(pending).length > 0);
  const wasObservingRef = useRef(canObserve);
  useEffect(() => {
    const resumed = canObserve && !wasObservingRef.current;
    wasObservingRef.current = canObserve;
    if (!canObserve || Object.keys(pending).length === 0) {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      const snapshot = Object.entries(pending);
      const results = await Promise.all(
        snapshot.map(async ([videoId, deletion]) => {
          try {
            return {
              kind: 'status' as const,
              response: await apiService.getVideoDeletion(videoId),
            };
          } catch (pollError) {
            const httpStatus = (pollError as any)?.response?.status;
            if (httpStatus === 404 || httpStatus === 410) {
              return {
                kind: 'gone' as const,
                videoId,
                deletionId: deletion.deletion_id,
              };
            }
            console.warn('Unable to refresh video deletion status:', pollError);
            return { kind: 'retry' as const };
          }
        }),
      );
      if (cancelled) {
        return;
      }

      updatePending((current) => results.reduce((state, result) => {
        if (result.kind === 'status') {
          return applyVideoDeletionStatus(state, result.response);
        }
        if (result.kind === 'gone') {
          return clearVideoDeletionIfMatching(
            state,
            result.videoId,
            result.deletionId,
          );
        }
        return state;
      }, current));
      timer = window.setTimeout(poll, retryDelaySeconds(pending) * 1000);
    };

    if (resumed) void poll();
    else timer = window.setTimeout(poll, retryDelaySeconds(pending) * 1000);
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [canObserve, pending, updatePending]);
};
